import React, { useState } from 'react';
import { Modal, DatePicker, Input, Typography, Col, Row, Space, Image, Divider } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ModalFooter } from '~/components/Modal';
// import jsonData from './lienWaiverData.json'; // Caminho do JSON

const { Text, Paragraph, Title } = Typography;
const today = new Date();

const getDay = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d) ? '' : d.getDate().toString();
};

const getMonth = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d) ? '' : d.toLocaleString('default', { month: 'long' });
};

const getYear = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return isNaN(d) ? '' : d.getFullYear().toString();
};

export default function LienWaiverContract({ visible, onCancel, onSave, selectedRecord }) {

  const imagemDefault = '/assets/images/default.png';
  const { lienWaiver, payAppClient } = selectedRecord || { lienWaiver: {}, payAppClient: {} };
  const { property } = payAppClient.project || {};
  const propertyAddress = property?.address;

  const initialData = {
    state: propertyAddress?.state || '',
    county: lienWaiver?.county || '',
    id: lienWaiver.id || '',
    owner: lienWaiver.owner || '',
    subjectProperty: lienWaiver.subjectProperty || '',
    value: lienWaiver.value || 0,
    paymentDate: lienWaiver.paymentDate || '',
    executedDay: getDay(lienWaiver.executionDate) || new Date().getDate().toString(),
    executedMonth: getMonth(lienWaiver.executionDate) || today.toLocaleString('en-US', { month: 'long' }), // today.toLocaleString('default', { month: 'long' }),
    executedYear: getYear(lienWaiver.executionDate) || today.getFullYear().toString(),
    contractor: lienWaiver.contractor || '',
    contractorSignature: lienWaiver.contractorSignature || null,
    by: lienWaiver.by || '',
    title: lienWaiver.title || '',
    retainage: lienWaiver.retainage || 0,
    disputedCharges: lienWaiver.disputedCharges || 0,
    notary: lienWaiver.notary || '',
    commissionExpiresDate: lienWaiver.commissionExpiresDate || null,
    notaryImage: lienWaiver.notaryImage || null,
  };

  const [state, setState] = useState(initialData.state);
  const [county, setCounty] = useState(initialData.county);
  const id = initialData.id;
  const [owner, setOwner] = useState(initialData.owner);
  const [subjectProperty, setSubjectProperty] = useState(initialData.subjectProperty);
  const [value, setValue] = useState(initialData.value);
  const [paymentDate, setPaymentDate] = useState(initialData.paymentDate);
  const [executedDay, setExecutedDay] = useState(initialData.executedDay);
  const [executedMonth, setExecutedMonth] = useState(initialData.executedMonth);
  const [executedYear, setExecutedYear] = useState(initialData.executedYear);
  const [contractor, setContractor] = useState(initialData.contractor);
  const contractorSignature = initialData.contractorSignature;
  const [by, setBy] = useState(initialData.by);
  const [title, setTitle] = useState(initialData.title);
  const [retainage, setRetainage] = useState(initialData.retainage);
  const [disputedCharges, setDisputedChanges] = useState(initialData.disputedCharges);
  const notary = initialData.notary;
  const commissionExpiresDate = initialData.commissionExpiresDate;
  const notaryImage = initialData.notaryImage;

  // transform commissionExpiresDate in 'month day, year' format, for example: 'April 5, 2023'
  const commissionExpiresDateFormatted2 = commissionExpiresDate
    ? (() => {
        const d = new Date(commissionExpiresDate);
        if (isNaN(d)) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return d.toLocaleDateString('en-US', options);
      })()
    : '';

  // transform paymentDate in 'month day, year' format, for example: 'April 5, 2023'
  const paymentDateFormatted2 = paymentDate
    ? (() => {
        const d = new Date(paymentDate);
        if (isNaN(d)) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return d.toLocaleDateString('en-US', options);
      })()
    : '';

  const handleSave = () => {
    // Clone selectedRecord to avoid mutation
    const updatedRecord = { ...selectedRecord };

    // Convert executedDay, executedMonth, and executedYear to a date string
    // Build executionDate in "YYYY-MM-DD HH:mm:ss" format
    let executionDate = '';
    if (executedYear && executedMonth && executedDay) {
      const monthNum = new Date(Date.parse(executedMonth + ' 1')).getMonth() + 1;
      const monthStr = monthNum.toString().padStart(2, '0');
      const dayStr = executedDay.toString().padStart(2, '0');
      executionDate = `${executedYear}-${monthStr}-${dayStr} 09:02:56`;
    }

    const paymentDateFormatted = paymentDate
      ? (() => {
          const d = new Date(paymentDate);
          if (isNaN(d)) return '';
          const year = d.getFullYear();
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const day = d
            .getDate()
            .toString()
            .padStart(2, '0');
          return `${year}-${month}-${day} 09:02:56`;
        })()
      : '';

    // Verify if commissionExpiresDate is a date string and format it like paymentDate
    const commissionExpiresDateFormatted = commissionExpiresDate
      ? (() => {
          const d = new Date(commissionExpiresDate);
          if (isNaN(d)) return '';
          const year = d.getFullYear();
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const day = d
            .getDate()
            .toString()
            .padStart(2, '0');
          return `${year}-${month}-${day} 09:02:56`;
        })()
      : '';

    // Verify if retainage and disputedCharges are numbers, if not, set them to 0
    const retainageValue = typeof retainage === 'number' ? retainage : parseFloat(retainage) || 0;
    const disputedChargesValue =
      typeof disputedCharges === 'number' ? disputedCharges : parseFloat(disputedCharges) || 0;
    const valueNumber = typeof value === 'number' ? value : parseFloat(value) || 0;

    // Update lienWaiver with new values
    updatedRecord.lienWaiver = {
      ...updatedRecord.lienWaiver,
      id,
      state,
      county,
      contractor,
      owner,
      subjectProperty,
      value: valueNumber,
      paymentDate: paymentDateFormatted,
      executionDate,
      retainage: retainageValue,
      disputedCharges: disputedChargesValue,
      contractorSignature,
      by,
      title,
      commissionExpiresDate: commissionExpiresDateFormatted,
      notary,
      notaryImage,
    };

    onSave(updatedRecord);
  };

  return (
    <Modal
      width="840px"
      title={<u>CONDITIONAL RELEASE OF LIEN</u>}
      open={visible}
      onCancel={onCancel}
      footer={<ModalFooter onOk={handleSave} onCancel={onCancel} />}
    >
      {/* <Container>
        <TextMd strong>CONDITIONAL RELEASE OF LIEN</TextMd>
      </Container> */}

      <Row justify="end">
        <br />
        <Title level={3}>Invoice #: {id}</Title>
      </Row>
      <Row>
        <Col span={12}>
          <Row>
            STATE OF {state}
          </Row>
          <Row>
            COUNTY OF {county}
          </Row>
        </Col>
        <Col span={12}>
          <Row>KNOW ALL MEN BY THESE PRESENTS:</Row>
        </Col>
      </Row>
      <br />
      <Row>
        <Paragraph>
          The undersigned,{contractor} (the “Contractor”), has performed the labor or furnished materials or done both, pursuant to either a Purchase
          Order or a Contract with {owner} (the “Owner”), in connection with the construction of {subjectProperty} (the “Subject Property”).
        </Paragraph>
      </Row>
      <Row style={{ paddingLeft: 40 }}>
        <Paragraph>
          As a result of the foregoing, ${value} is due and payable to the Contractor, for the period ending {paymentDateFormatted2} (the “Payment Date”). This amount represents all sums due Contractor, except as hereinafter noted, for
          materials supplied, and/or labor performed to the Payment Date in connection with the construction of
          improvements on the Subject Property.
        </Paragraph>
        <Paragraph>
          In consideration of the payment of the above stated sum, the receipt of which is hereby acknowledged,
          Contractor hereby waives, relinquishes, and releases any and all liens, rights, and interests (including,
          without limitation, all interest in and to mechanic’s and materialmen’s liens), owned, claimed or held, to be
          owned, claimed or held by Contractor in and to the Subject Property and to the improvements now or hereafter
          constructed thereon, and does hereby release, discharge and acquit the Owner, its successors and assigns, from
          all claims, debts, and demand by reason of the labor performed and/or materials furnished by Contractor prior
          to and including the Payment Date, except as herein provided.
        </Paragraph>
        <Paragraph>
          The undersigned has actual knowledge that all bills owed by Contractor as of the Payment Date for materials
          furnished and labor performed in connection with the construction have been fully paid and satisfied or will
          be fully paid and satisfied out of the sum stated above. Contractor does further guarantee that if for any
          reason a lien or liens are filed for materials or labor against the Subject Property by virtue of Contractor’s
          participation in the construction of said improvements, or that of any individual or entity Contractor has
          subcontracted with or procured materials from, Contractor will immediately obtain a settlement of such lien or
          liens and obtain and furnish Owner, its successors and assigns, a release thereof or a bond for release
          thereof, and if Contractor cannot obtain such a release, indemnify the Owner, its successors and assigns, for
          any and all costs the Owner, its successors and assigns, may incur in removing said lien or liens.
        </Paragraph>
      </Row>
      <Divider dashed type="horizontal" orientation="right"><span style={{ fontSize: 12, color: '#888' }}>Page 2</span></Divider>
      <Row>
        <Paragraph>
          Contractor hereby acknowledged and agree that but for the representations ans agreements contained herein
          concerning the total amounts due ans owing, the Owner would not have made the payment receipted herein above
          and Qwnwe is relying upon such representations and agreements.
        </Paragraph>
      </Row>
      <br />
      <Row>
        <Paragraph>
          The only amounts not included within the waiver and release contained herein as of the Payment Date are ${retainage} (retainage), and
          ${disputedCharges} (disputed changes).
        </Paragraph>
      </Row>
      <br />
      <Row>
        <Paragraph>
          Executed this {executedDay} day of {executedMonth}, {executedYear}.
        </Paragraph>
      </Row>
      <br />
      <Row>
        <Col span={12}>
          <Row>
            <Space direction="vertical">
              <>"Contractor"</>
              {contractor}
              <>(Company Name)</>
            </Space>
          </Row>
          <br />
          <br />
          <Row>
            <Paragraph>
              By: {by}
            </Paragraph>
          </Row>
          <Row>
            <Paragraph>
              Title: {title}
            </Paragraph>
          </Row>
        </Col>
        <Col span={12}>
          <Row>
            <Space direction="vertical">
              <Image
                style={{ objectFit: 'contain' }}
                height={200}
                alt="Signature"
                src={`${process.env.REACT_APP_BACKEND_UPLOAD_URL}/${contractorSignature?.name}` || imagemDefault}
                fallback={imagemDefault}
              />
            </Space>
          </Row>
        </Col>
      </Row>Notary Signature
      <Row>
        <Paragraph>
          THE STATE OF {state}
          COUNTY OF {county}
        </Paragraph>
      </Row>
      <Row>
        <Paragraph>
          This instrument was acknowledged before me by {by}, President of {contractor}.
        </Paragraph>
      </Row>
      <br />
      <Row>
        <Col span={12}>
          <Row>
            <Paragraph>
              Notary Public in and for the
              <br />
              State of {state}
            </Paragraph>
          </Row>
          <Row>
            <Paragraph>My Commission Expires: {commissionExpiresDateFormatted2}</Paragraph>
          </Row>
          <Row>
            {notary}
            <br />
            Printed name of Notary
          </Row>
          <Row style={{ marginTop: 20 }}>
            <Image
              style={{ objectFit: 'contain' }}
              height={200}
              alt="Notary Stamp"
              src={`${process.env.REACT_APP_BACKEND_UPLOAD_URL}/${notaryImage?.name}` || imagemDefault}
              fallback={imagemDefault}
            />
          </Row>
        </Col>
      </Row>
    </Modal>
  );
}
